import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import './Charts.css'

/**
 * LineChart Component
 *
 * A simple SVG-based line chart with optional area fill.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array
 * @param {string} props.labelKey - Key for labels in data
 * @param {string} props.valueKey - Key for values in data
 * @param {string} props.title - Chart title
 * @param {boolean} props.showDots - Show data points
 * @param {boolean} props.showArea - Fill area under line
 * @param {boolean} props.showGrid - Show grid lines
 * @param {string} props.color - Line color
 * @param {Function} props.formatValue - Value formatter function
 * @param {number} props.height - Chart height in pixels
 * @param {boolean} props.smooth - Use smooth curves
 */
const LineChart = ({
	data = [],
	labelKey = 'label',
	valueKey = 'value',
	title,
	showDots = true,
	showArea = false,
	showGrid = true,
	color = 'var(--color-primary)',
	formatValue = (v) => v,
	height = 300,
	smooth = true,
}) => {
	// Chart dimensions
	const padding = { top: 20, right: 20, bottom: 40, left: 50 }
	const width = 600
	const chartWidth = width - padding.left - padding.right
	const chartHeight = height - padding.top - padding.bottom

	// Calculate min/max values
	const { minValue, maxValue } = useMemo(() => {
		if (!data.length) return { minValue: 0, maxValue: 0 }
		const values = data.map((d) => d[valueKey] || 0)
		return {
			minValue: Math.min(...values),
			maxValue: Math.max(...values),
		}
	}, [data, valueKey])

	// Scale functions
	const scaleX = (index) => {
		if (data.length <= 1) return chartWidth / 2
		return (index / (data.length - 1)) * chartWidth
	}

	const scaleY = (value) => {
		const range = maxValue - minValue || 1
		return chartHeight - ((value - minValue) / range) * chartHeight
	}

	// Generate path
	const linePath = useMemo(() => {
		if (!data.length) return ''

		const points = data.map((d, i) => ({
			x: scaleX(i),
			y: scaleY(d[valueKey] || 0),
		}))

		if (smooth && points.length > 2) {
			// Generate smooth curve
			return points.reduce((path, point, i) => {
				if (i === 0) return `M ${point.x},${point.y}`

				const prev = points[i - 1]
				const cpx = (prev.x + point.x) / 2
				return `${path} Q ${prev.x},${prev.y} ${cpx},${(prev.y + point.y) / 2} T ${point.x},${point.y}`
			}, '')
		}

		return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ')
	}, [data, valueKey, smooth])

	// Generate area path
	const areaPath = useMemo(() => {
		if (!showArea || !linePath) return ''
		return `${linePath} L ${scaleX(data.length - 1)},${chartHeight} L 0,${chartHeight} Z`
	}, [linePath, showArea, data.length, chartHeight])

	// Grid lines
	const yGridLines = useMemo(() => {
		if (!showGrid) return []
		const lines = []
		const step = (maxValue - minValue) / 4
		for (let i = 0; i <= 4; i++) {
			const value = minValue + step * i
			lines.push({
				value,
				y: scaleY(value),
			})
		}
		return lines
	}, [showGrid, minValue, maxValue])

	if (!data.length) {
		return (
			<div className="chart chart--empty">
				<p>No data available</p>
			</div>
		)
	}

	return (
		<div className="chart line-chart">
			{title && <h4 className="chart__title">{title}</h4>}

			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="line-chart__svg"
				preserveAspectRatio="xMidYMid meet"
			>
				<g transform={`translate(${padding.left},${padding.top})`}>
					{/* Grid Lines */}
					{showGrid && (
						<g className="line-chart__grid">
							{yGridLines.map((line, i) => (
								<g key={i}>
									<line
										x1={0}
										y1={line.y}
										x2={chartWidth}
										y2={line.y}
										stroke="var(--color-border)"
										strokeDasharray="4,4"
									/>
									<text
										x={-10}
										y={line.y}
										textAnchor="end"
										dominantBaseline="middle"
										fill="var(--color-text-muted)"
										fontSize="12"
									>
										{formatValue(Math.round(line.value))}
									</text>
								</g>
							))}
						</g>
					)}

					{/* Area */}
					{showArea && (
						<path
							d={areaPath}
							fill={color}
							fillOpacity="0.1"
							className="line-chart__area"
						/>
					)}

					{/* Line */}
					<path
						d={linePath}
						fill="none"
						stroke={color}
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="line-chart__line"
					/>

					{/* Dots */}
					{showDots &&
						data.map((d, i) => (
							<g key={i} className="line-chart__dot-group">
								<circle
									cx={scaleX(i)}
									cy={scaleY(d[valueKey] || 0)}
									r="4"
									fill={color}
									stroke="white"
									strokeWidth="2"
									className="line-chart__dot"
								/>
								<title>
									{d[labelKey]}: {formatValue(d[valueKey])}
								</title>
							</g>
						))}

					{/* X-axis Labels */}
					{data.map((d, i) => {
						// Show fewer labels if too many data points
						if (data.length > 10 && i % Math.ceil(data.length / 10) !== 0 && i !== data.length - 1) {
							return null
						}
						return (
							<text
								key={i}
								x={scaleX(i)}
								y={chartHeight + 25}
								textAnchor="middle"
								fill="var(--color-text-muted)"
								fontSize="11"
							>
								{d[labelKey]}
							</text>
						)
					})}
				</g>
			</svg>
		</div>
	)
}

LineChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	labelKey: PropTypes.string,
	valueKey: PropTypes.string,
	title: PropTypes.string,
	showDots: PropTypes.bool,
	showArea: PropTypes.bool,
	showGrid: PropTypes.bool,
	color: PropTypes.string,
	formatValue: PropTypes.func,
	height: PropTypes.number,
	smooth: PropTypes.bool,
}

export default LineChart
